import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom } from 'rxjs';
import { OrderService } from '../../../_services/order.service';
import { AlertService } from '../../../_services/alert.service';
import { CartService } from '../../../_services/cart.service';

export interface AbandonedCartTotals {
  count: number;
  value: number;
  notContacted: number;
  returning: number;
}

interface AbandonedCart {
  cartId: number;
  createdAt: string;
  updatedAt: string;
  value: number;
  customer: { name: string | null; email: string | null; phone: string | null; postCode: string | null };
  account: { userid: number; company: string | null; trade: boolean; tier: string; orders: number; spent: number } | null;
  itemCount: number;
  items: { name: string; quantity: number; unitPrice: number; image: string | null }[];
  lastFollowUp: string | null;
  lastFollowUpDate: string | null;
  lastFollowUpBy: string | null;
  // Follow-up box state (not from the server).
  noteOpen?: boolean;
  noteText?: string;
  noteSaving?: boolean;
  starting?: boolean;
}

// What create-sale shows when it opens with a basket loaded from a web cart.
export interface CartLeadState {
  cartLead: {
    name: string | null;
    email: string | null;
    phone: string | null;
    postCode: string | null;
    account: any | null;
    dropped: string[];
    priceChanges: { name: string; was: number; now: number }[];
    shortOfStock: { name: string; wanted: number; available: number }[];
  };
}

// Shoppers who reached the website checkout, typed an email or phone, and
// didn't order - the warmest leads there are. Same layout as the lost sales
// panel; the follow-up is saved on the cart (there's no order to note), and
// "Start sale" loads their basket, at today's prices, into a new sale.
@Component({
    selector: 'app-abandoned-carts',
    imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule],
    templateUrl: './abandoned-carts.component.html'
})
export class AbandonedCartsComponent implements OnInit {
  @Input() initialDays = 30;
  @Output() closed = new EventEmitter<void>();
  @Output() totalsChanged = new EventEmitter<AbandonedCartTotals>();

  readonly windows = [1, 3, 7, 14, 30, 60, 90];
  days = 30;
  contacted: 'ALL' | 'NO' | 'YES' = 'NO';
  sort: 'recent' | 'value' = 'recent';
  search = '';

  items: AbandonedCart[] = [];
  count = 0;
  totals: AbandonedCartTotals | null = null;
  pageIndex = 0;
  readonly pageSize = 20;
  loading = true;
  loadingMore = false;
  loadError = false;
  exporting = false;
  private token = 0;
  private searchTimer: any;

  constructor(
    private orderService: OrderService,
    private alertService: AlertService,
    private cartService: CartService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.days = this.initialDays;
    this.load(true);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closed.emit();
  }

  windowLabel(d: number): string {
    return d + 'd';
  }

  private request(): any {
    return { days: this.days, contacted: this.contacted, sort: this.sort, search: this.search.trim(), pageSize: this.pageSize, pageIndex: this.pageIndex };
  }

  load(reset: boolean): void {
    const token = ++this.token;
    if (reset) {
      this.pageIndex = 0;
      this.loading = true;
    } else {
      this.loadingMore = true;
    }
    this.loadError = false;
    this.orderService.getAbandonedCarts(this.request()).subscribe({
      next: (r: any) => {
        if (token !== this.token) return;
        this.items = reset ? r.items : [...this.items, ...r.items];
        this.count = r.count;
        this.totals = r.totals;
        this.totalsChanged.emit(r.totals);
        this.loading = false;
        this.loadingMore = false;
      },
      error: (error) => {
        if (token !== this.token) return;
        this.loading = false;
        this.loadingMore = false;
        this.loadError = true;
        this.alertService.alert(error?.error?.message || 'Could not load abandoned carts', 'error');
      },
    });
  }

  loadMore(): void {
    this.pageIndex++;
    this.load(false);
  }

  get hasMore(): boolean {
    return this.items.length < this.count;
  }

  setDays(d: number): void {
    this.days = d;
    this.load(true);
  }

  setContacted(c: 'ALL' | 'NO' | 'YES'): void {
    this.contacted = c;
    this.load(true);
  }

  setSort(s: 'recent' | 'value'): void {
    this.sort = s;
    this.load(true);
  }

  onSearch(): void {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.load(true), 350);
  }

  // ---- display ----
  ago(iso: string | null): string {
    if (!iso) return '';
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
    if (mins < 60) return `${Math.max(mins, 1)} min ago`;
    if (mins < 24 * 60) return `${Math.floor(mins / 60)} h ago`;
    const days = Math.floor(mins / (24 * 60));
    if (days === 1) return 'yesterday';
    if (days < 31) return `${days} days ago`;
    return `${Math.round(days / 30)} months ago`;
  }

  // Under a day old: the shopper is most likely still deciding - call now.
  isHot(c: AbandonedCart): boolean {
    return Date.now() - new Date(c.updatedAt).getTime() < 86_400_000;
  }

  displayName(c: AbandonedCart): string {
    return c.customer.name || c.customer.email || c.customer.phone || 'Unknown shopper';
  }

  firstName(c: AbandonedCart): string {
    return (c.customer.name || '').split(' ')[0] || 'there';
  }

  private basketLine(c: AbandonedCart): string {
    const first = c.items[0]?.name;
    if (!first) return 'your basket';
    return c.itemCount > 1 ? `the ${first} and ${c.itemCount - 1} other item${c.itemCount > 2 ? 's' : ''} in your basket` : `the ${first} in your basket`;
  }

  // UK numbers to international form for WhatsApp ("07..." -> "447...").
  waLink(phone: string, c: AbandonedCart): string {
    let d = phone.replace(/[^\d+]/g, '');
    if (d.startsWith('+')) d = d.slice(1);
    else if (d.startsWith('00')) d = d.slice(2);
    else if (d.startsWith('0')) d = '44' + d.slice(1);
    const text = `Hi ${this.firstName(c)}, it's Nustone - I saw you were looking at ${this.basketLine(c)} on our website. Can I help with anything, or put the order through for you?`;
    return `https://wa.me/${d}?text=${encodeURIComponent(text)}`;
  }

  mailLink(c: AbandonedCart): string {
    const subject = 'Your Nustone basket';
    const body = `Hi ${this.firstName(c)},\n\nI noticed you were looking at ${this.basketLine(c)} on our website but didn't get to finish your order. Is there anything I can help with - delivery, quantities or samples? I'm happy to put the order through for you over the phone.\n\nKind regards`;
    return `mailto:${c.customer.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  // ---- actions ----
  toggleNote(c: AbandonedCart): void {
    c.noteOpen = !c.noteOpen;
    if (c.noteOpen && c.noteText === undefined) c.noteText = '';
  }

  saveNote(c: AbandonedCart): void {
    const text = (c.noteText ?? '').trim();
    if (!text) return;
    c.noteSaving = true;
    this.orderService.saveAbandonedCartFollowUp(c.cartId, text).subscribe({
      next: () => {
        c.noteSaving = false;
        this.markFollowedUp(c, text, 'You');
        c.noteOpen = false;
        c.noteText = '';
        this.alertService.alert('Follow-up saved', 'success');
      },
      error: (error) => {
        c.noteSaving = false;
        this.alertService.alert(error?.error?.message || 'Could not save the follow-up', 'error');
      },
    });
  }

  private markFollowedUp(c: AbandonedCart, text: string, by: string): void {
    const wasNew = !c.lastFollowUpDate;
    c.lastFollowUp = text;
    c.lastFollowUpDate = new Date().toISOString();
    c.lastFollowUpBy = by;
    if (wasNew && this.totals) {
      this.totals = { ...this.totals, notContacted: Math.max(0, this.totals.notContacted - 1) };
      this.totalsChanged.emit(this.totals);
    }
    if (this.contacted === 'NO') {
      this.items = this.items.filter((x) => x.cartId !== c.cartId);
      this.count = Math.max(0, this.count - 1);
    }
  }

  // Loads their basket (re-priced by the server) into the new sale page and
  // opens it with their account picked, when they have one.
  startSale(c: AbandonedCart): void {
    const current = this.cartService.getCartDataFromLocalStorage();
    if (current?.items?.length && !confirm('The new sale page already has a basket in progress. Replace it with this cart?')) return;
    c.starting = true;
    this.orderService.getAbandonedCartBasket(c.cartId).subscribe({
      next: (b: any) => {
        c.starting = false;
        if (!b.items?.length) {
          this.alertService.alert('Nothing in this cart can be sold any more', 'error');
          return;
        }
        const total = b.items.reduce((s: number, i: any) => s + i.unitPrice * i.quantity, 0);
        this.cartService.setCart({ items: b.items, total, totalFormatted: total.toFixed(2), weight: 0, appliedCouponCodes: [] });
        const note = 'Opened as a back-office sale';
        this.orderService.saveAbandonedCartFollowUp(c.cartId, note).subscribe({ error: () => {} });
        this.markFollowedUp(c, note, 'You');
        const state: CartLeadState = {
          cartLead: {
            ...b.customer,
            account: b.account,
            dropped: b.dropped ?? [],
            priceChanges: b.priceChanges ?? [],
            shortOfStock: b.shortOfStock ?? [],
          },
        };
        this.closed.emit();
        this.router.navigate(['/sales/create'], { state });
      },
      error: (error) => {
        c.starting = false;
        this.alertService.alert(error?.error?.message || 'Could not load this cart', 'error');
      },
    });
  }

  async copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.alertService.alert('Copied', 'success');
    } catch {
      this.alertService.alert('Could not copy', 'error');
    }
  }

  // ---- export ----
  async exportToExcel(): Promise<void> {
    if (this.exporting || !this.count) return;
    this.exporting = true;
    try {
      const rows: AbandonedCart[] = [];
      const cap = Math.min(this.count, 2000);
      for (let pageIndex = 0; rows.length < cap; pageIndex++) {
        const r: any = await firstValueFrom(this.orderService.getAbandonedCarts({ ...this.request(), pageSize: 100, pageIndex }));
        if (!r.items?.length) break;
        rows.push(...r.items);
        if (r.items.length < 100) break;
      }
      await this.writeExport(rows);
    } catch {
      this.alertService.alert('Failed to build the export', 'error');
    } finally {
      this.exporting = false;
    }
  }

  private async writeExport(rows: AbandonedCart[]): Promise<void> {
    const ExcelJS: any = await import('exceljs');
    const Workbook = (ExcelJS.default ?? ExcelJS).Workbook;
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet('Abandoned Carts', { views: [{ state: 'frozen', ySplit: 1 }] });
    sheet.columns = [
      { header: 'Name', key: 'name', width: 22 },
      { header: 'Company', key: 'company', width: 20 },
      { header: 'Email', key: 'email', width: 30 },
      { header: 'Phone', key: 'phone', width: 16 },
      { header: 'Postcode', key: 'postCode', width: 10 },
      { header: 'Last Active', key: 'updatedAt', width: 18 },
      { header: 'Cart Value', key: 'value', width: 12 },
      { header: 'Products', key: 'items', width: 50 },
      { header: 'Past Orders', key: 'orders', width: 12 },
      { header: 'Trade', key: 'trade', width: 8 },
      { header: 'Last Follow-up', key: 'lastFollowUp', width: 30 },
      { header: 'Followed Up By', key: 'lastFollowUpBy', width: 16 },
      { header: 'Followed Up On', key: 'lastFollowUpDate', width: 18 },
    ];
    const header = sheet.getRow(1);
    header.height = 22;
    header.eachCell((cell: any) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF171717' } };
      cell.alignment = { vertical: 'middle' };
    });
    for (const c of rows) {
      const row = sheet.addRow({
        name: c.customer.name || '',
        company: c.account?.company || '',
        email: c.customer.email || '',
        phone: c.customer.phone || '',
        postCode: c.customer.postCode || '',
        updatedAt: c.updatedAt ? formatDate(c.updatedAt, 'dd MMM yyyy HH:mm', 'en') : '',
        value: c.value,
        items: c.items.map((i) => `${i.quantity} x ${i.name}`).join(', ') + (c.itemCount > c.items.length ? ` +${c.itemCount - c.items.length} more` : ''),
        orders: c.account?.orders ?? 0,
        trade: c.account?.trade ? 'Yes' : '',
        lastFollowUp: c.lastFollowUp || '',
        lastFollowUpBy: c.lastFollowUpBy || '',
        lastFollowUpDate: c.lastFollowUpDate ? formatDate(c.lastFollowUpDate, 'dd MMM yyyy', 'en') : '',
      });
      row.eachCell((cell: any) => {
        cell.alignment = { vertical: 'top', wrapText: true };
        cell.border = { bottom: { style: 'thin', color: { argb: 'FFE5E5E5' } } };
      });
    }
    const buffer = await workbook.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `abandoned-carts-${formatDate(new Date(), 'yyyy-MM-dd', 'en')}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }
}
